import { groupNamePresenter, taskNamePresenter } from '@kaiizen/kai-presenters';
import {
  getScanWithUpdatedLineItem,
  getScanWithUpdatedOption,
  getScanWithUpdatedTask,
  ScanGroupOptimized,
  ScanLineItemOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';

import { lineItemPriceIsFromProduct } from './voiceScopeSnapshot';
import type { VoiceScopeSetTaskValueCommand } from './voiceScopeTypes';

export type VoiceScopeAgentProvenance = {
  agentUpdatedAt: number;
  agentUpdateAcceptedAt: number;
};

/** Builds KaiAgent provenance from proposal and acceptance timestamps. */
export const buildVoiceScopeAgentProvenance = (
  proposedAtMs: number,
  acceptedAtMs: number = Date.now(),
): VoiceScopeAgentProvenance => ({
  agentUpdatedAt: proposedAtMs,
  agentUpdateAcceptedAt: acceptedAtMs,
});

/** Task flags applied after every approved KaiAgent voice-scope edit. */
export const voiceScopeTaskAppliedUpdates = (
  provenance: VoiceScopeAgentProvenance,
): Partial<ScanTaskOptimized> => ({
  userAdded: true,
  notApplicable: false,
  agentUpdatedAt: provenance.agentUpdatedAt,
  agentUpdateAcceptedAt: provenance.agentUpdateAcceptedAt,
});

export const markVoiceScopeTaskApplied = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  provenance: VoiceScopeAgentProvenance,
): ScanRootOptimized => {
  const task = scan.groupsById[groupId]?.tasksById[taskId];
  if (!task) return scan;
  return getScanWithUpdatedTask(scan, groupId, taskId, voiceScopeTaskAppliedUpdates(provenance));
};

export const voiceScopeLineItemAppliedUpdates = (
  lineItem: ScanLineItemOptimized,
  updates: Partial<ScanLineItemOptimized>,
  provenance: VoiceScopeAgentProvenance,
): Partial<ScanLineItemOptimized> => ({
  ...updates,
  ...(lineItem.notApplicable ? { notApplicable: false } : {}),
  agentUpdatedAt: lineItem.agentUpdatedAt ?? provenance.agentUpdatedAt,
  agentUpdateAcceptedAt: provenance.agentUpdateAcceptedAt,
});

/** Stamps acceptance provenance on the option and line items an addOption command touched. */
export const stampVoiceScopeAgentProvenanceOnAddedOption = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  optionTemplateId: string,
  provenance: VoiceScopeAgentProvenance,
): ScanRootOptimized => {
  const task = requireVoiceScopeTask(requireVoiceScopeGroup(scan, groupId), taskId);
  const option = task.options.find(entry => entry.template.id === optionTemplateId);
  if (!option) return scan;

  let next = getScanWithUpdatedOption(scan, groupId, taskId, option.id, {
    agentUpdatedAt: option.agentUpdatedAt ?? provenance.agentUpdatedAt,
    agentUpdateAcceptedAt: provenance.agentUpdateAcceptedAt,
  });

  for (const lineItem of option.lineItems) {
    next = getScanWithUpdatedLineItem(next, groupId, taskId, option.id, lineItem.id, {
      agentUpdatedAt: lineItem.agentUpdatedAt ?? provenance.agentUpdatedAt,
      agentUpdateAcceptedAt: provenance.agentUpdateAcceptedAt,
    });
  }

  return next;
};

export const requireVoiceScopeGroup = (scan: ScanRootOptimized, groupId: string): ScanGroupOptimized => {
  const group = scan.groupsById[groupId];
  if (!group) throw new Error(`Group ${groupId} is not in the scope`);
  return group;
};

export const requireVoiceScopeTask = (group: ScanGroupOptimized, taskId: string): ScanTaskOptimized => {
  const task = group.tasksById[taskId];
  if (!task) throw new Error(`Task ${taskId} is not in ${groupNamePresenter(group)}`);
  return task;
};

const setLineItemValue = (
  scan: ScanRootOptimized,
  groupId: string,
  task: ScanTaskOptimized,
  command: VoiceScopeSetTaskValueCommand & { optionId: string; lineItemId: string },
  provenance: VoiceScopeAgentProvenance,
) => {
  const lineItem = task.optionsById[command.optionId]?.lineItemsById[command.lineItemId];
  if (!lineItem) throw new Error(`${taskNamePresenter(task)} no longer has that line item`);
  if (!lineItem.template.values?.[command.valueName]) throw new Error(`${lineItem.template.name} has no value named ${command.valueName}`);
  if (command.valueName === 'price' && lineItemPriceIsFromProduct(lineItem)) {
    throw new Error(`${lineItem.template.name} takes its price from its product`);
  }
  return getScanWithUpdatedLineItem(
    scan,
    groupId,
    task.id,
    command.optionId,
    command.lineItemId,
    voiceScopeLineItemAppliedUpdates(lineItem, {
      values: { [command.valueName]: { setValue: command.value } },
    }, provenance),
  );
};

/** Applies a setTaskValue voice command to the scan root. */
export const applyVoiceScopeSetTaskValue = (
  scan: ScanRootOptimized,
  groupId: string,
  command: VoiceScopeSetTaskValueCommand,
  provenance: VoiceScopeAgentProvenance,
): ScanRootOptimized => {
  const task = requireVoiceScopeTask(requireVoiceScopeGroup(scan, groupId), command.taskId);
  if (command.optionId && command.lineItemId) {
    return setLineItemValue(scan, groupId, task, { ...command, optionId: command.optionId, lineItemId: command.lineItemId }, provenance);
  }
  if (!task.template.values?.[command.valueName]) {
    throw new Error(`${taskNamePresenter(task)} has no value named ${command.valueName}`);
  }
  return getScanWithUpdatedTask(scan, groupId, task.id, { values: { [command.valueName]: { setValue: command.value } } });
};
